/**
 * clientValidation.ts
 *
 * Pure helpers for validating the New/Edit Client form and detecting
 * duplicate clients before they hit the database.
 *
 * Why app-level dedup (not a DB UNIQUE constraint): the production `clients`
 * table already contains duplicate phones/emails/addresses, so a hard unique
 * index can't be applied without a cleanup pass. These helpers give immediate,
 * field-aware blocking with a message that names the existing client.
 */
import type { Client, Lead } from '../types';
import { ProjectStatus, LeadStatus } from '../types';
import type { Traducir } from '../i18n/idioma';

// ── Normalizers ────────────────────────────────────────────────
// Used for both validation and duplicate comparison so the same value
// always normalizes the same way on create and edit.

/** Digits only. Phones are compared on their last 10 digits so that a leading
 *  country code ("1") or formatting ("(555) 123-4567") doesn't hide a match. */
export const normalizePhone = (raw: string | undefined | null): string => {
    const digits = (raw || '').replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
};

export const normalizeEmail = (raw: string | undefined | null): string =>
    (raw || '').trim().toLowerCase();

/** Lowercase, collapse internal whitespace, strip trailing commas/periods so
 *  "123 Main St." and "123  main st" compare equal. */
export const normalizeAddress = (raw: string | undefined | null): string =>
    (raw || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[.,\s]+$/, '');

/** Sin acentos, sin puntos y con los espacios apretados, para que "Juan Pérez",
 *  "juan perez" y "Juan  Peréz." sean la misma persona. Se queda en lo
 *  mecánico: nada de apodos ni de parecidos. Fallar por defecto a "no son la
 *  misma" solo deja pasar un alta; equivocarse al revés bloquea a alguien que
 *  tiene todo el derecho a estar. */
export const normalizeName = (raw: string | undefined | null): string =>
    (raw || '')
        .normalize('NFD').replace(/\p{Diacritic}/gu, '')
        .toLowerCase().replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();

// Reasonable email shape. Intentionally permissive — we only reject obvious junk.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** ¿Esto PARECE una dirección? Un número y una longitud mínima, nada más.
 *
 *  No verifica que exista: obra nueva sin número asignado, calles rurales y
 *  lotes que Google todavía no conoce son justo donde más se usa la app. Lo que
 *  frena es lo que no es una dirección de ninguna manera —"asdkjhaskd", "TBD",
 *  "casa"—, que es lo que se colaba por el cuadro de texto de la conversión.
 *
 *  Vive aquí y no dentro de `validateClientForm` porque ahora hay DOS sitios que
 *  piden una dirección: el formulario de cliente y el cuadro que aparece al
 *  convertir un prospecto. Los dos tienen que aceptar y rechazar lo mismo. */
export const isPlausibleAddress = (raw: string | undefined | null): boolean => {
    const address = (raw || '').trim();
    return /\d/.test(address) && address.length >= 5;
};

export interface ClientFormValues {
    name: string;
    phone: string;
    email: string;
    address: string;
    gateCode: string;
    preferredContact: 'Phone' | 'Email' | 'Text';
}

export interface ClientFormContext {
    /** True when the current address text came from picking a Google suggestion. */
    addressSelected: boolean;
    /** True when address autocomplete is actually working. When false (API not
     *  loaded / not configured) we relax to a plausible-text check so the user
     *  is never fully locked out of creating a client. */
    addressSearchAvailable: boolean;
}

export type ClientFormErrors = Partial<Record<keyof ClientFormValues, string>>;

/** Returns a map of field -> error message. Empty object means valid. */
export const validateClientForm = (
    v: ClientFormValues,
    ctx: ClientFormContext,
): ClientFormErrors => {
    const errors: ClientFormErrors = {};

    if (v.name.trim().length < 2) {
        errors.name = 'Enter the client or business name.';
    }

    const phoneDigits = normalizePhone(v.phone);
    if (phoneDigits.length < 10) {
        errors.phone = 'Enter a valid phone number (at least 10 digits).';
    }

    // Required on clients (leads stay lenient — see validateLeadForm below).
    // A client is someone you will be sending paperwork to: proposals, contracts
    // and receipts all travel by email, so a client record without one is a dead
    // end the moment there is something to send.
    const email = v.email.trim();
    if (!email) {
        errors.email = 'Enter an email address — proposals and receipts are sent here.';
    } else if (!EMAIL_RE.test(email)) {
        errors.email = 'Enter a valid email address.';
    }

    /* Direccion: se pide COMPLETA, no VERIFICADA POR GOOGLE.
     *
     * Antes, si habia sugerencias, era obligatorio elegir una. Esa regla deja
     * fuera precisamente los trabajos donde mas se usa la app: obra nueva sin
     * numero asignado, calles rurales, lotes que Google todavia no conoce. El
     * contratista tenia la direccion en la mano, escrita por el cliente, y la
     * app no le dejaba guardar.
     *
     * Elegir de la lista sigue siendo el camino rapido (y el que geocodifica
     * solo). Escribirla a mano tambien vale, siempre que parezca una direccion
     * de verdad: numero y una longitud minima. */
    const address = v.address.trim();
    if (!address) {
        errors.address = 'Enter the property address.';
    } else if (!isPlausibleAddress(address)) {
        errors.address = 'Enter a complete street address (include the street number).';
    }

    return errors;
};

// ── Lead form ──────────────────────────────────────────────────
// Leads are captured fast, often mid-phone-call, so the bar is lower than for
// a client: no address verification, email optional. But name and a reachable
// phone are the whole point of a lead — without them the record is dead weight.

export interface LeadFormValues {
    name: string;
    phone: string;
    email: string;
    address: string;
}

export type LeadFormErrors = Partial<Record<keyof LeadFormValues, string>>;

/** Returns a map of field -> error message. Empty object means valid. */
export const validateLeadForm = (v: LeadFormValues): LeadFormErrors => {
    const errors: LeadFormErrors = {};

    if (v.name.trim().length < 2) {
        errors.name = 'Enter the prospect name.';
    }

    if (normalizePhone(v.phone).length < 10) {
        errors.phone = 'Enter a valid phone number (at least 10 digits).';
    }

    const email = v.email.trim();
    if (email && !EMAIL_RE.test(email)) {
        errors.email = 'Enter a valid email address.';
    }

    /* La direccion NO se exige aqui.
     *
     * El comentario de arriba ya decia que el liston es mas bajo para un
     * prospecto —se apunta al vuelo, muchas veces en mitad de una llamada— pero
     * el codigo la pedia igual, y encima el campo se rotulaba solo "Address"
     * mientras el de al lado decia "Email (Optional)". El error solo aparecia al
     * enviar, cuando la persona ya habia colgado.
     *
     * Se pide al CONVERTIR en cliente, que es cuando de verdad hace falta:
     * ahi es donde se mandan propuestas y se planifican rutas. Ver el prompt en
     * LeadsView.handleConvert. */

    return errors;
};

// ── Deleting a client ──────────────────────────────────────────
// Deleting a client does NOT cascade: linked estimates and projects survive
// with a clientId pointing at a row that no longer exists. Until a real
// cascade exists at the database level, the least we can do is say exactly
// what will be orphaned — and refuse outright while work is still running.

/** Minimal shapes so this stays a pure helper with no view dependencies. */
interface DeletionLink { clientId: string }
interface DeletionProject extends DeletionLink { status: string }

export interface ClientDeletionCheck {
    /** True when deletion must be refused — show `message` and stop. */
    blocked: boolean;
    /** Ready-to-show dialog body, either the refusal or the consequences. */
    message: string;
    activeProjectCount: number;
    projectCount: number;
    estimateCount: number;
}

/**
 * Decides whether a client can be deleted and builds the dialog copy naming
 * the real consequences, instead of the previous vague "may lose context".
 */
export const describeClientDeletion = (
    clientName: string,
    clientId: string,
    projects: DeletionProject[],
    estimates: DeletionLink[],
): ClientDeletionCheck => {
    const clientProjects = projects.filter(p => p.clientId === clientId);
    const activeProjectCount = clientProjects.filter(p => p.status === ProjectStatus.IN_PROGRESS).length;
    const projectCount = clientProjects.length;
    const estimateCount = estimates.filter(e => e.clientId === clientId).length;

    if (activeProjectCount > 0) {
        return {
            blocked: true,
            activeProjectCount, projectCount, estimateCount,
            message: `${clientName} has ${activeProjectCount} job${activeProjectCount === 1 ? '' : 's'} still in progress. Complete or cancel ${activeProjectCount === 1 ? 'it' : 'them'} first, then delete the client.`,
        };
    }

    const orphans = [
        estimateCount > 0 ? `${estimateCount} estimate${estimateCount === 1 ? '' : 's'}` : '',
        projectCount > 0 ? `${projectCount} project${projectCount === 1 ? '' : 's'}` : '',
    ].filter(Boolean);

    return {
        blocked: false,
        activeProjectCount, projectCount, estimateCount,
        message: orphans.length > 0
            ? `Delete ${clientName}? ${orphans.join(' and ')} will stay in your records but will no longer be linked to any client. This cannot be undone.`
            : `Delete ${clientName}? They have no estimates or projects on file. This cannot be undone.`,
    };
};

export interface DuplicateMatch {
    field: 'phone' | 'email' | 'address';
    label: string;       // human-readable field name for the message
    existing: Client;
}

/* ── La misma persona en la misma dirección ───────────────────────────────
 *
 * Esto NO es la regla de duplicados de la agenda —esa la lleva
 * `findDuplicateContact`, y desde agosto de 2026 una dirección repetida basta
 * para bloquear—. Esto responde a otra pregunta, la de `findDuplicateClient`:
 * «este prospecto que estoy convirtiendo, ¿es ya este cliente?».
 *
 * Ahí la dirección sola NO puede valer, y la diferencia no es de criterio sino
 * de consecuencia: lo que `findDuplicateClient` devuelve se usa como la ficha a
 * la que enganchar las propuestas del prospecto. Con la dirección sola, ganar
 * un prospecto en un edificio donde ya hay un cliente le colgaba su trabajo a
 * la ficha del VECINO, sin decir nada. Por eso aquí hace falta el nombre. */
const samePersonAtSameAddress = (
    candidate: { name?: string; address?: string },
    record: { name?: string | null; address?: string | null },
): boolean => {
    const address = normalizeAddress(candidate.address);
    const name = normalizeName(candidate.name);
    if (!address || !name) return false;
    return normalizeAddress(record.address) === address && normalizeName(record.name) === name;
};

/**
 * ¿Cuál de estos clientes ES este contacto? Compara teléfono, correo y
 * nombre-más-dirección, en ese orden. Los valores vacíos se ignoran.
 *
 * Es una comprobación de IDENTIDAD, no la puerta de duplicados: la usa
 * `convertLeadToClient` para no crear dos veces la ficha del mismo prospecto y
 * para saber a qué cliente traspasarle sus propuestas. Quien quiera bloquear un
 * alta repetida busca `findDuplicateContact`, que mira las dos agendas y es más
 * estricto con la dirección y con el nombre.
 *
 * El NOMBRE solo NO entra aquí, y esa asimetría con `findDuplicateContact` es
 * deliberada. Allí un tocayo BLOQUEA el alta, que es un rechazo que se lee y se
 * arregla escribiendo el nombre completo. Aquí un tocayo sería una FICHA
 * ELEGIDA: ganar un prospecto llamado "Jose" le colgaría sus propuestas al otro
 * "Jose" que ya estaba, en silencio y sin vuelta atrás.
 */
export const findDuplicateClient = (
    clients: Client[],
    candidate: { name?: string; phone?: string; email?: string; address?: string },
    excludeId?: string,
): DuplicateMatch | null => {
    const phone = normalizePhone(candidate.phone);
    const email = normalizeEmail(candidate.email);

    for (const c of clients) {
        if (excludeId && c.id === excludeId) continue;

        if (phone && normalizePhone(c.phone) === phone) {
            return { field: 'phone', label: 'phone number', existing: c };
        }
        if (email && normalizeEmail(c.email) === email) {
            return { field: 'email', label: 'email', existing: c };
        }
        if (samePersonAtSameAddress(candidate, c)) {
            return { field: 'address', label: 'property address', existing: c };
        }
    }
    return null;
};

// ── Duplicados entre las DOS agendas ───────────────────────────
//
// Clientes y prospectos son la misma libreta de contactos partida en dos, y
// hasta ahora cada mitad solo se miraba a sí misma: `findDuplicateClient` solo
// veía clientes, y los prospectos no se comprobaban en absoluto. Con eso se
// podían tener tres fichas de la misma persona —dos prospectos y un cliente—
// con el mismo teléfono, y ninguna pantalla que lo dijera.
//
// Bloquea el TELÉFONO repetido, el CORREO repetido y la DIRECCIÓN repetida.
// Cualquiera de los tres, en la agenda que sea.
//
// La dirección entró en la lista en agosto de 2026, por decisión de producto.
// Antes solo contaba junto al mismo nombre, para no dejar fuera a dos vecinos
// del mismo edificio; a cambio, la misma casa se podía dar de alta tantas veces
// como se escribiera un nombre distinto, y eso es lo que estaba pasando de
// verdad en la libreta. Se cambió el lado por el que se falla: ahora una
// propiedad sale una sola vez.
//
// El precio hay que decirlo en el mensaje, porque lo van a encontrar: cuando el
// autocompletado de Google devuelve la calle SIN el número de apartamento, dos
// vecinos producen la MISMA cadena y el segundo queda bloqueado. La salida es
// el número de unidad ("1 Elm St Apt 4"), y por eso el texto del bloqueo la
// nombra en vez de limitarse a decir que ya existe.
//
// CONVERTIR UN PROSPECTO ES LA EXCEPCIÓN (`permitirDireccionRepetida`), y en
// agosto de 2026 se decidió que ahí la dirección repetida AVISA en vez de
// bloquear. El motivo es que ahí no hay ninguna salida: quien convierte no está
// dando de alta un contacto que se pueda corregir o abandonar, está cerrando un
// trato que YA existe, y el trabajo hecho para ese prospecto —sus propuestas, su
// proyecto— solo se traspasa al ganarlo. Bloquear dejaba la tarjeta sin poder
// avanzar y el trabajo colgando de un prospecto para siempre; y por el camino
// del arrastre a la columna Ganado ni siquiera había un cuadro donde reescribir
// la dirección. Se avisa nombrando la ficha que ya la tiene, y se deja pasar.
// Teléfono, correo y nombre siguen bloqueando igual en ese camino.

export type ContactKind = 'client' | 'lead';

/** Lo mínimo para comparar: sirve igual para una fila de `clients` que de
 *  `leads`, y `kind` es lo que permite decir de cuál de las dos se habla. */
export interface ContactRecord {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    kind: ContactKind;
}

export interface ContactDuplicate {
    field: 'phone' | 'email' | 'address' | 'name';
    existing: ContactRecord;
}

export const clientContactRecord = (c: Pick<Client, 'id' | 'name' | 'phone' | 'email' | 'address'>): ContactRecord =>
    ({ id: c.id, name: c.name, phone: c.phone, email: c.email, address: c.address, kind: 'client' });

export const leadContactRecord = (l: Pick<Lead, 'id' | 'name' | 'phone' | 'email' | 'address'>): ContactRecord =>
    ({ id: l.id, name: l.name, phone: l.phone, email: l.email, address: l.address, kind: 'lead' });

/**
 * Un prospecto GANADO ya tiene su ficha de cliente detrás —ganarlo es lo que la
 * crea— y se queda en el tablero, cerrado, como registro de que ese trato se
 * cerró. Dejarlo en la comparación solo produce bloqueos que mandan a una ficha
 * de solo lectura, cuando el cliente equivalente cubre el mismo teléfono y el
 * mismo correo y es el que sí se puede abrir y editar.
 */
export const isLeadEligibleForDedup = (l: Pick<Lead, 'status'>): boolean =>
    l.status !== LeadStatus.WON;

/* ── El NOMBRE repetido también bloquea ───────────────────────────────────
 *
 * Entró en agosto de 2026, por decisión de producto y por el mismo motivo que
 * la dirección: en la libreta de verdad estaban apareciendo tres fichas de
 * "Jose" —una recién creada al ganar un prospecto— con direcciones distintas y
 * sin teléfono que las cruzara, y nada en la app lo decía.
 *
 * Hay que saber lo que cuesta, porque se va a notar: dos clientes que de verdad
 * se llamen igual son un caso normal, y el segundo NO se va a poder dar de alta
 * tal cual. La salida es escribir el nombre completo —"Jose Quiñones", "Jose M.
 * Ramírez"—, que es lo que el mensaje del bloqueo pide.
 *
 * Se compara con `normalizeName`, que solo iguala lo mecánico (acentos, puntos,
 * mayúsculas, espacios): "Jose" y "Jose Quiñones" siguen siendo dos personas
 * distintas, así que basta con distinguirlos para poder guardar. */

/**
 * Busca en las dos agendas a la vez el contacto que choca con `candidate`.
 *
 * Los clientes se recorren ANTES que los prospectos a propósito: cuando la
 * misma persona está en las dos listas, el mensaje tiene que mandar a la ficha
 * de cliente, que es la que manda.
 *
 * El NOMBRE se mira en una segunda pasada, después de recorrer la libreta
 * entera por teléfono, correo y dirección. Si no, un tocayo cualquiera que
 * apareciera antes en la lista se llevaría el mensaje por delante del contacto
 * que choca por teléfono —que es el mismo de verdad, y el que hay que nombrar.
 *
 * `exclude` evita que una ficha choque consigo misma al editarla; hay una llave
 * por agenda porque un id de cliente y uno de prospecto no son comparables.
 */
export const findDuplicateContact = (
    records: ContactRecord[],
    candidate: { name?: string; phone?: string; email?: string; address?: string },
    exclude: { clientId?: string; leadId?: string } = {},
    opciones: { permitirNombreRepetido?: boolean; permitirDireccionRepetida?: boolean } = {},
): ContactDuplicate | null => {
    const phone = normalizePhone(candidate.phone);
    const email = normalizeEmail(candidate.email);
    const address = opciones.permitirDireccionRepetida ? '' : normalizeAddress(candidate.address);
    const name = opciones.permitirNombreRepetido ? '' : normalizeName(candidate.name);
    if (!phone && !email && !address && !name) return null;

    const ordered = [
        ...records.filter(r => r.kind === 'client'),
        ...records.filter(r => r.kind === 'lead'),
    ];
    const visible = ordered.filter(r => {
        const excluded = r.kind === 'client' ? exclude.clientId : exclude.leadId;
        return !(excluded && r.id === excluded);
    });

    for (const r of visible) {
        if (phone && normalizePhone(r.phone) === phone) return { field: 'phone', existing: r };
        if (email && normalizeEmail(r.email) === email) return { field: 'email', existing: r };
        if (address && normalizeAddress(r.address) === address) return { field: 'address', existing: r };
    }

    if (name) {
        for (const r of visible) {
            if (normalizeName(r.name) === name) return { field: 'name', existing: r };
        }
    }
    return null;
};

/** Marca del error de duplicado. Se mira este código y NO el texto del mensaje:
 *  el mensaje está traducido y cambia con el idioma, así que un `test(/…/)`
 *  sobre él deja de reconocerlo en cuanto alguien pone la app en español. */
export const DUPLICATE_CONTACT_CODE = 'DUPLICATE_CONTACT';

export const duplicateContactError = (message: string): Error => {
    const err = new Error(message);
    (err as Error & { code?: string }).code = DUPLICATE_CONTACT_CODE;
    return err;
};

/** ¿Este error es un bloqueo por duplicado (validación) y no un fallo real? */
export const isDuplicateContactError = (err: unknown): boolean =>
    typeof err === 'object' && err !== null
    && (err as { code?: string }).code === DUPLICATE_CONTACT_CODE;

/**
 * Texto del bloqueo, ya traducido y nombrando la ficha que ocupa el dato.
 *
 * Las llamadas a `t()` van con la clave LITERAL, una por caso, para que
 * `i18n/marcadores.test.ts` pueda comprobar que los marcadores casan. Con una
 * clave armada por concatenación esa guardia no ve nada.
 */
export const describeDuplicateContact = (t: Traducir, dup: ContactDuplicate): string => {
    const nombre = dup.existing.name.trim() || t('crm.duplicado.sinNombre');
    if (dup.existing.kind === 'client') {
        if (dup.field === 'phone') return t('crm.duplicado.cliente.telefono', { nombre });
        if (dup.field === 'email') return t('crm.duplicado.cliente.email', { nombre });
        if (dup.field === 'name') return t('crm.duplicado.cliente.nombre', { nombre });
        return t('crm.duplicado.cliente.direccion', { nombre });
    }
    if (dup.field === 'phone') return t('crm.duplicado.prospecto.telefono', { nombre });
    if (dup.field === 'email') return t('crm.duplicado.prospecto.email', { nombre });
    if (dup.field === 'name') return t('crm.duplicado.prospecto.nombre', { nombre });
    return t('crm.duplicado.prospecto.direccion', { nombre });
};

/**
 * Aviso —NO bloqueo— de que esa dirección ya está en otra ficha.
 *
 * Es la versión suave de `describeDuplicateContact`: aquel texto manda a abrir
 * la otra ficha «en vez de crear otra», que es la salida correcta cuando se está
 * dando de alta un contacto y aquí sería un mal consejo —el trato ya está
 * cerrado y hay que crear la ficha igual—. Este solo dice quién la tiene y cómo
 * distinguirlas si de verdad son dos.
 */
export const describeAddressAlsoUsed = (t: Traducir, dup: ContactDuplicate): string => {
    const nombre = dup.existing.name.trim() || t('crm.duplicado.sinNombre');
    return dup.existing.kind === 'client'
        ? t('prospectos.convertir.direccion.tambienEnCliente', { nombre })
        : t('prospectos.convertir.direccion.tambienEnProspecto', { nombre });
};

export interface AddressCheck {
    /** Impide guardar. Solo una cosa: que lo escrito no parezca una dirección. */
    error: string | null;
    /** No impide nada. Se enseña para que quien convierte lo sepa antes. */
    warning: string | null;
}

/**
 * Qué decir de la dirección que se escribe al convertir un prospecto, mientras
 * se escribe. Separa lo que FRENA de lo que solo hay que SABER.
 *
 * Está aquí y no dentro del cuadro de diálogo porque tiene que coincidir con lo
 * que hace la mutación al guardar: `error` es lo mismo que rechaza
 * `validateClientForm`, y `warning` es exactamente el caso que la mutación deja
 * pasar (`permitirDireccionRepetida`). Escritas dos veces, el cuadro habría
 * acabado frenando algo que la escritura acepta —o al revés, y ese fallo llega
 * como un toast rojo cuando el usuario ya cerró el diálogo y no puede corregir.
 */
export const checkConversionAddress = (
    t: Traducir,
    records: ContactRecord[],
    address: string,
    leadId: string,
): AddressCheck => {
    if (!isPlausibleAddress(address)) {
        return { error: t('prospectos.convertir.faltadireccion.invalida'), warning: null };
    }
    const dup = findDuplicateContact(records, { address }, { leadId });
    return { error: null, warning: dup ? describeAddressAlsoUsed(t, dup) : null };
};